import { complimentCountForLog, hydrateComplimentData } from './complimentEvents.js'
import { concentrationPresentation, resolvedConcentrationDisplay } from './concentrationResolver.js'
import { fragranceLeaning } from './fragranceLeaning.js'

export const COLLECTION_SEASON_FILTERS = Object.freeze([
  { label: 'Spring / Summer', values: ['spring', 'summer'] },
  { label: 'Fall / Winter', values: ['fall', 'autumn', 'winter'] },
])

const SEASON_VALUES_BY_LABEL = new Map(
  COLLECTION_SEASON_FILTERS.map(option => [option.label, new Set(option.values)]),
)

function normalized(value) {
  return String(value || '').trim().toLocaleLowerCase()
}

function arrayValue(value) {
  return Array.isArray(value) ? value : []
}

export function filterableConcentration(row) {
  const presentation = concentrationPresentation(row)
  if (!['resolved', 'resolved with conflict'].includes(presentation.status)) return null
  const display = resolvedConcentrationDisplay(row).trim()
  return display && !['unknown', 'ambiguous'].includes(normalized(display)) ? display : null
}

function rowConcentrations(row) {
  const grouped = arrayValue(row?.resolved_concentrations).filter(Boolean)
  if (grouped.length > 0) return grouped
  const resolved = filterableConcentration(row)
  return resolved ? [resolved] : []
}

function titleCaseAccord(value) {
  return String(value || '')
    .trim()
    .replace(/[_-]+/g, ' ')
    .replace(/\b\p{L}/gu, letter => letter.toLocaleUpperCase())
}

export function collectionFacetOptions(rows = []) {
  const concentrations = new Map()
  const accordCounts = new Map()

  for (const row of rows) {
    for (const display of rowConcentrations(row)) {
      const key = normalized(display)
      if (key && !concentrations.has(key)) concentrations.set(key, display)
    }

    const rowAccords = new Set(arrayValue(row?.accord_tags).map(normalized).filter(Boolean))
    for (const accord of rowAccords) {
      accordCounts.set(accord, (accordCounts.get(accord) || 0) + 1)
    }
  }

  return {
    concentrations: [...concentrations.values()].sort((a, b) => a.localeCompare(b)),
    accords: [...accordCounts.entries()]
      .sort(([a, countA], [b, countB]) => (countB - countA) || a.localeCompare(b))
      .slice(0, 12)
      .map(([accord]) => titleCaseAccord(accord)),
  }
}

export function complimentCountsByFragrance(wearLogs = [], complimentEvents = []) {
  const counts = new Map()
  const hydratedLogs = hydrateComplimentData(wearLogs, { events: complimentEvents })

  for (const log of hydratedLogs) {
    const fragranceId = log?.fragrance_id
    const count = complimentCountForLog(log)
    if (!fragranceId || count <= 0) continue
    counts.set(fragranceId, (counts.get(fragranceId) || 0) + count)
  }

  return counts
}

function matchesSeasonGroup(row, selectedSeasonGroups) {
  if (selectedSeasonGroups.length !== 1) return true
  const accepted = SEASON_VALUES_BY_LABEL.get(selectedSeasonGroups[0])
  if (!accepted) return true
  return arrayValue(row?.seasons).some(season => accepted.has(normalized(season)))
}

function intersects(left, right) {
  if (right.length === 0) return true
  const available = new Set(left.map(normalized))
  return right.some(value => available.has(normalized(value)))
}

export function applyCollectionFilters(rows = [], filters = {}) {
  const selectedSeasonGroups = arrayValue(filters.seasons)
  const selectedConcentrations = arrayValue(filters.concentrations)
  const selectedAccords = arrayValue(filters.accords)
  const selectedLeanings = arrayValue(filters.leanings)

  const filtered = rows.filter(row => (
    (!filters.house || row.house === filters.house)
    && matchesSeasonGroup(row, selectedSeasonGroups)
    && intersects(rowConcentrations(row), selectedConcentrations)
    && intersects(arrayValue(row?.accord_tags), selectedAccords)
    && intersects([fragranceLeaning(row)].filter(Boolean), selectedLeanings)
    && (!filters.mostComplimented || Number(row?.compliment_count || 0) > 0)
  ))

  if (!filters.mostComplimented) return filtered
  return filtered
    .map((row, index) => ({ row, index }))
    .sort((a, b) => (
      Number(b.row?.compliment_count || 0) - Number(a.row?.compliment_count || 0)
      || a.index - b.index
    ))
    .map(({ row }) => row)
}
